import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text, TextInput } from '@mantine/core';
import CardModal from '@/components/card-modal';
import { useCreateSavedReport } from '@/hooks/api-hooks';
import { SavedReport } from '@turizi/shared';

interface SavePresetModalProps {
    opened: boolean;
    onClose: () => void;
    params: SavedReport['params'];
    onSaveSuccess?: (presetId: string) => void;
}

export default function SavePresetModal({
    opened,
    onClose,
    params,
    onSaveSuccess
}: SavePresetModalProps) {
    const { t } = useTranslation();
    const createPreset = useCreateSavedReport();
    const [newPresetName, setNewPresetName] = useState('');

    const handleSavePreset = async () => {
        if (!newPresetName.trim()) return;

        try {
            const result = await createPreset.mutateAsync({
                name: newPresetName.trim(),
                params,
            });
            if (result.data) {
                onSaveSuccess?.(result.data.id.toString());
            }
            setNewPresetName('');
            onClose();
        } catch (err) {
            // Error notification is handled by the hook
        }
    };

    return (
        <CardModal
            opened={opened}
            onClose={() => {
                onClose();
                setNewPresetName('');
            }}
            header={<Text className="font-semibold text-lg">{t('save_preset_dialog_title')}</Text>}
            size="md"
        >
            <div className="space-y-4">
                <TextInput
                    placeholder={t('name')}
                    value={newPresetName}
                    onChange={(event) => setNewPresetName(event.currentTarget.value)}
                    data-autofocus
                />
                <div className="flex justify-end gap-3 mt-4">
                    <Button
                        size='xs'
                        color='black'
                        variant="subtle"
                        onClick={() => {
                            onClose();
                            setNewPresetName('');
                        }}>
                        {t('cancel')}
                    </Button>
                    <Button
                        size='xs'
                        color='black'
                        loading={createPreset.isPending}
                        onClick={handleSavePreset}
                        disabled={!newPresetName.trim()}
                    >
                        {t('save')}
                    </Button>
                </div>
            </div>
        </CardModal>
    );
}
